import { LitElement, html, customElement, property, TemplateResult, internalProperty } from "lit-element";
import { Teaser } from "../models/Teaser";
import "./teaser-item.ts";
import { isNullOrWhitespace } from "../util/isNullOrWhitespace";
import { TeaserItem } from "./teaser-item";

@customElement("teaser-list-widget")
export class TeaserListWidget extends LitElement {
  // optionally listen to url changes
  @property({ type: String }) public teaserWidgetCorrelationId!: string;
  // true if teaser shall flip content and image on hover
  @property({ type: Boolean }) public isFlippable!: boolean;
  // true if teaser shall display title and description text
  @property({ type: Boolean }) public showTitleAndDescription!: boolean;
  // true if teaser shall display a line below the image
  @property({ type: Boolean }) public showBottomLine!: boolean;
  // teasers as array
  @property({ type: Array }) public teasers: Array<Teaser> = [];
  // text to display if there are no results for the selection
  @property({ type: String }) public noResultsForSelectionText!: string;

  @internalProperty() private visibleTeasers: Array<Teaser & { visible: boolean }> = [];

  private matchBelowLarge!: MediaQueryList;
  private matchMedium!: MediaQueryList;
  private observer!: IntersectionObserver;
  private timersToFlip: Map<string, number> = new Map<string, number>();

  public connectedCallback(): void {
    super.connectedCallback();
    const breakpointLarge = window.getComputedStyle(this).getPropertyValue("--breakpoint-lg");
    this.matchBelowLarge = window.matchMedia(`(max-width: ${breakpointLarge})`);
    const breakpointMedium = window.getComputedStyle(this).getPropertyValue("--breakpoint-md");
    this.matchMedium = window.matchMedia(`(min-width: ${breakpointMedium})`);

    this.observer = new IntersectionObserver(this.handleTeasersInViewPort(), {
      threshold: 0.8,
    });
  }

  public async firstUpdated(): Promise<void> {
    // check which teasers should be visible on page load
    this.onUrlChanged();
    if (!isNullOrWhitespace(this.teaserWidgetCorrelationId)) {
      // only listen to url changes when an id is present.
      window.addEventListener("popstate", () => this.onUrlChanged());
    }
    // only register flipping logic when the items are actually flippable
    if (this.isFlippable) {
      // Give the browser a chance to paint
      await new Promise((r) => setTimeout(r, 0));
      this.querySelectorAll(".teaser-container teaser-item").forEach((element) => this.observer?.observe(element));
    }
  }

  public disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("popstate", () => this.onUrlChanged());
    this.observer.disconnect();
  }

  // disable shadow DOM
  protected createRenderRoot(): this {
    return this;
  }

  // Implement `render` to define a template for your element.
  protected render(): TemplateResult | "" {
    const visibleTeasers = this.visibleTeasers.filter((teaser) => teaser.visible);
    if (visibleTeasers.length === 0) {
      return html`${this.noResultsForSelectionText}`;
    }

    return html`<div class="row teaser-container">
      ${visibleTeasers.map(
        (teaser) => html`<teaser-item
          class="col-12 col-md-6 col-lg-3 teaser-item"
          ?lazyImage="${isNullOrWhitespace(this.teaserWidgetCorrelationId)}"
          ?isFlippable="${this.isFlippable}"
          ?showTitleAndDescription="${this.showTitleAndDescription}"
          ?showBottomLine="${this.showBottomLine}"
          title="${teaser.title}"
          description="${teaser.description}"
          frontImageUrl="${teaser.frontImageUrl}"
          backImageUrl="${teaser.backImageUrl}"
          frontHeadline="${teaser.frontHeadline}"
          backHeadline="${teaser.backHeadline}"
          frontBackgroundColor="${teaser.frontBackgroundColor}"
          backBackgroundColor="${teaser.backBackgroundColor}"
          linkUrl="${teaser.linkUrl}"
          linkTarget="${teaser.linkTarget}"
          .dateString="${teaser.date}"
        ></teaser-item>`
      )}
    </div>`;
  }

  private handleTeasersInViewPort(): IntersectionObserverCallback {
    return (entries): void => {
      let intersectingIndex = 0;
      entries.forEach((entry) => {
        const item = entry.target as TeaserItem;
        if (item.isFlipped || !this.matchBelowLarge.matches) {
          // Either the teaser is already flipped or we are on a screen bigger than or equal to large.
          return;
        }

        if (entry.isIntersecting && !this.timersToFlip.has(item.id)) {
          this.timersToFlip.set(
            item.id,
            window.setTimeout(
              () => {
                item.doTheFlip();
                this.timersToFlip.delete(item.id);
              },
              this.matchMedium.matches ? 1500 + 360 * intersectingIndex : 1500
            )
          );
          ++intersectingIndex;
          return;
        }

        if (entry.isIntersecting && this.timersToFlip.has(item.id)) {
          window.clearTimeout(this.timersToFlip.get(item.id));
          this.timersToFlip.delete(item.id);
        }
      });
    };
  }

  private onUrlChanged(): void {
    if (isNullOrWhitespace(this.teaserWidgetCorrelationId)) {
      this.visibleTeasers = this.teasers.map((teaser) => ({ ...teaser, visible: true }));
      return;
    }

    const urlParameters = new URLSearchParams(window.location.search);
    const urlParamName1 = `category-1-${this.teaserWidgetCorrelationId}`;
    const urlParamName2 = `category-2-${this.teaserWidgetCorrelationId}`;
    const urlParamName3 = `category-3-${this.teaserWidgetCorrelationId}`;
    const codeNameFilters = {
      1: urlParameters.get(urlParamName1) || "",
      2: urlParameters.get(urlParamName2) || "",
      3: urlParameters.get(urlParamName3) || "",
    };

    this.visibleTeasers = this.teasers.map((teaser) => ({
      ...teaser,
      visible: this.shouldTeaserBeVisible(teaser, codeNameFilters),
    }));
  }

  private shouldTeaserBeVisible(teaser: Teaser, codeNameFilters: { 1: string; 2: string; 3: string }): boolean {
    const isInFilter1 = codeNameFilters[1] === "" ? true : teaser.categories.includes(codeNameFilters[1]);
    const isInFilter2 = codeNameFilters[2] === "" ? true : teaser.categories.includes(codeNameFilters[2]);
    const isInFilter3 = codeNameFilters[3] === "" ? true : teaser.categories.includes(codeNameFilters[3]);
    return isInFilter1 && isInFilter2 && isInFilter3;
  }
}
